import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import CourseCard from "./components/CourseCard";
import RecommendedSection from "./components/RecommendedSection";
import TopperChoiceSection from "./components/TopperChoiceSection";
import BooksForYouSection from "./components/BooksForYouSection";
import ExploreCoursesBannerCarousel from "./components/ExploreCoursesBannerCarousel";
import VarientCard from "../../containers/logged_user_components/user_store/user_store_content_component/store_components/package_offering_component/Card/varients-card/VarientCard";
import {
  useDashboardExploreStoreQuery,
  useDashboardHomeSliderBannersQuery,
} from "../StudyDashboard/hooks/useStudyDashboardQueries";
import { useHorizontalCarousel } from "./hooks/useHorizontalCarousel";
import {
  getExploreCategoryKey,
  isExploreSectionEnabled,
  packageMatchesCategory,
} from "./utils/exploreStore";
import { shouldOpenVariantModal, shouldShowBuyNow } from "./utils/explorePackage";
import { buyHandbookNow } from "../DashboardBooks/utils/handbookCheckout";
import { navigateToPackagePage, navigateToHandbookPage, EXPLORE_COURSES_PATH } from "../../custom_utilities/dashboardBannerNavigation";
import { trackWebEngageBuyNow } from "../../custom_utilities/webengage/webengageService";
import uiActions from "../../redux/ui/action";
import { setStoreCurrentHandbookDetails } from "../../redux/handbook/HandbookSlice";
import SectionCarouselNav from "./components/SectionCarouselNav";
import {
  getExploreBookAnchorId,
  getExploreCourseAnchorId,
  readExploreCoursesScrollState,
  saveExploreCoursesScrollState,
} from "./utils/exploreCoursesScrollRestore";
import "./DashboardCourses.css";

const BOOKS_CATEGORY_KEY = "books";

const DashboardCourses = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const exploreQuery = useDashboardExploreStoreQuery();
  const sliderBannersQuery = useDashboardHomeSliderBannersQuery();
  const [activeCategoryKey, setActiveCategoryKey] = useState(
    () => readExploreCoursesScrollState()?.activeCategoryKey ?? null
  );

  const exploreData = exploreQuery.data;
  const categories = useMemo(
    () => exploreData?.overall?.category_list ?? [],
    [exploreData]
  );
  const packages = useMemo(
    () => exploreData?.overall?.packages ?? exploreData?.overall?.package ?? [],
    [exploreData]
  );
  const recommended = exploreData?.recommended;
  const topperChoice = exploreData?.topper_choice;
  const handbook = exploreData?.handbook;
  const sliderBanners = sliderBannersQuery.data ?? [];
  const [variantPackage, setVariantPackage] = useState(null);
  const [highlightedAnchorId, setHighlightedAnchorId] = useState(
    () => readExploreCoursesScrollState()?.anchorId ?? null
  );

  const startLoading = useCallback(() => {
    dispatch(uiActions.startLoading());
  }, [dispatch]);

  const finishLoading = useCallback(() => {
    dispatch(uiActions.finishLoading());
  }, [dispatch]);

  const openPackageVariants = useCallback((course) => {
    if (!shouldOpenVariantModal(course)) return;
    setVariantPackage(course);
  }, []);

  const closePackageVariants = useCallback(() => {
    setVariantPackage(null);
  }, []);

  const saveScrollBeforeLeave = useCallback(
    (anchorId) => {
      setHighlightedAnchorId(anchorId);
      saveExploreCoursesScrollState({
        anchorId,
        activeCategoryKey,
      });
    },
    [activeCategoryKey]
  );

  useEffect(() => {
    if (exploreQuery.isLoading) {
      dispatch(uiActions.startLoading());
      return () => {
        dispatch(uiActions.finishLoading());
      };
    }
    dispatch(uiActions.finishLoading());
    return undefined;
  }, [dispatch, exploreQuery.isLoading]);

  useEffect(() => {
    if (!categories.length) return;
    setActiveCategoryKey((current) => {
      const keys = categories.map(getExploreCategoryKey);
      if (
        current &&
        current !== BOOKS_CATEGORY_KEY &&
        keys.some((key) => String(key) === String(current))
      ) {
        return current;
      }
      return getExploreCategoryKey(categories[0]);
    });
  }, [categories]);

  const isBooksTab = activeCategoryKey === BOOKS_CATEGORY_KEY;

  const filteredPackages = useMemo(() => {
    if (isBooksTab) return [];
    if (!packages.length) return [];
    if (activeCategoryKey == null) return packages;
    const matched = packages.filter((item) =>
      packageMatchesCategory(item, activeCategoryKey)
    );
    return matched.length ? matched : packages;
  }, [packages, activeCategoryKey, isBooksTab]);

  const listItems = filteredPackages;
  const hasCarouselItems = listItems.length > 0;

  const {
    viewportRef: coursesViewportRef,
    trackRef: coursesTrackRef,
    trackOffset: coursesTrackOffset,
    canScrollLeft,
    canScrollRight,
    hasOverflow: hasCoursesOverflow,
    scrollByPage: scrollCourses,
  } = useHorizontalCarousel(listItems.length, activeCategoryKey);

  const handleCategoryClick = (categoryKey) => {
    if (categoryKey === BOOKS_CATEGORY_KEY) {
      history.push("/dashboard/courses/books");
      return;
    }
    setHighlightedAnchorId(null);
    setActiveCategoryKey(categoryKey);
  };

  const openPackage = (course, section = "main") => {
    if (!course?.package_id) return;
    saveScrollBeforeLeave(getExploreCourseAnchorId(course.package_id, section));
    navigateToPackagePage(history, course.package_id, {
      returnTo: EXPLORE_COURSES_PATH,
    });
  };

  const buyPackage = (course, section = "main") => {
    if (!shouldShowBuyNow(course)) return;
    // Variant packages open a modal — fire buy_now only on final variant Buy Now.
    if (shouldOpenVariantModal(course)) {
      openPackageVariants(course);
      return;
    }
    trackWebEngageBuyNow(course, { card_section: section });
    openPackage(course, section);
  };

  const getPackageCardHandlers = (course, section = "main") => ({
    onDetails: () => openPackage(course, section),
    onBuy: () => buyPackage(course, section),
    onOpenVariants: openPackageVariants,
  });

  const openBook = (book) => {
    if (!book?.p_id) return;
    saveScrollBeforeLeave(getExploreBookAnchorId(book.p_id));
    dispatch(setStoreCurrentHandbookDetails(book));
    navigateToHandbookPage(history, book.p_id, {
      returnTo: EXPLORE_COURSES_PATH,
    });
  };

  const buyBook = (book) => {
    saveScrollBeforeLeave(getExploreBookAnchorId(book.p_id));
    buyHandbookNow(history, dispatch, book, { returnTo: EXPLORE_COURSES_PATH });
  };

  if (exploreQuery.isLoading) {
    return <div className="dashboard-courses dashboard-courses--loading" aria-busy="true" />;
  }

  return (
    <div className="dashboard-courses">
      <ExploreCoursesBannerCarousel banners={sliderBanners} />

      <section className="dashboard-courses__intro">
        <div className="dashboard-courses__intro-main">
          <h1 className="dashboard-courses__title">Motion Courses</h1>

          {categories.length ? (
            <div className="dashboard-courses__tabs" role="tablist" aria-label="Course categories">
              {categories.map((category) => {
                const categoryKey = getExploreCategoryKey(category);
                return (
                  <button
                    key={`${categoryKey}-${category.category_name}`}
                    type="button"
                    role="tab"
                    aria-selected={activeCategoryKey === categoryKey}
                    className={`dashboard-courses__tab${
                      activeCategoryKey === categoryKey ? " is-active" : ""
                    }`}
                    onClick={() => handleCategoryClick(categoryKey)}
                  >
                    {category.category_name}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {hasCarouselItems && hasCoursesOverflow ? (
          <SectionCarouselNav
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
            onPrev={() => scrollCourses(-1)}
            onNext={() => scrollCourses(1)}
            ariaLabel="Course list navigation"
          />
        ) : null}
      </section>

      <section className="dashboard-courses__list" aria-label="Course list">
        {hasCarouselItems ? (
          <div ref={coursesViewportRef} className="dashboard-courses__viewport">
            <div
              ref={coursesTrackRef}
              className="dashboard-courses__track"
              style={{ transform: `translate3d(-${coursesTrackOffset}px, 0, 0)` }}
            >
              {filteredPackages.map((course) => (
                <CourseCard
                  key={course.package_id}
                  course={course}
                  section="main"
                  highlightedAnchorId={highlightedAnchorId}
                  {...getPackageCardHandlers(course, "main")}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="dashboard-courses__empty">No courses available right now.</p>
        )}
      </section>

      {isExploreSectionEnabled(recommended?.show_recommended) ? (
        <RecommendedSection
          title={recommended.title}
          packages={recommended.packages ?? []}
          highlightedAnchorId={highlightedAnchorId}
          getPackageCardHandlers={(course) => getPackageCardHandlers(course, "recommended")}
        />
      ) : null}

      {isExploreSectionEnabled(topperChoice?.show_topper_choice) ? (
        <TopperChoiceSection
          topperChoice={topperChoice}
          highlightedAnchorId={highlightedAnchorId}
          getPackageCardHandlers={(course) => getPackageCardHandlers(course, "topper")}
        />
      ) : null}

      {isExploreSectionEnabled(handbook?.show_handbook) ? (
        <BooksForYouSection
          handbook={handbook}
          highlightedAnchorId={highlightedAnchorId}
          onOpenBook={openBook}
          onBuyBook={buyBook}
        />
      ) : null}

      {variantPackage ? (
        <VarientCard
          show
          onHide={closePackageVariants}
          packageId={variantPackage.package_id}
          startLoading={startLoading}
          finishLoading={finishLoading}
          history={history}
        />
      ) : null}
    </div>
  );
};

export default DashboardCourses;
